import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Chart from "chart.js/auto";
import ReactECharts from "echarts-for-react";
import Select from "react-select";
import { generateAllMonths } from "../utils/generateAllMonths";
import "./StatsMeetings.css";

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
    return date.toISOString().slice(0, 10);
};

const exportCsv = (filename, headers, rows) => {
    const escape = (value) => {
        const text = String(value ?? "");
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

function StatsMeetings({ statsData: initialStatsData, onClose }) {
    const [statsData, setStatsData] = useState(initialStatsData || {});
    const [lodges, setLodges] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [selectedLodge, setSelectedLodge] = useState("");
    const [selectedEventType, setSelectedEventType] = useState("");
    const [excludedEventTypes, setExcludedEventTypes] = useState([]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sankeyModal, setSankeyModal] = useState(false);
    const [sankeyData, setSankeyData] = useState({ nodes: [], links: [] });
    const [chartSize, setChartSize] = useState({ width: 980, height: 560 });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const eventTypeChartRef = useRef(null);
    const eventTypeChartInstance = useRef(null);
    const echartRef = useRef(null);

    useEffect(() => {
        const today = new Date();
        const twentyYearsAgo = new Date();
        twentyYearsAgo.setFullYear(twentyYearsAgo.getFullYear() - 20);
        setStartDate(twentyYearsAgo.toISOString().slice(0, 10));
        setEndDate(today.toISOString().slice(0, 10));
    }, []);

    useEffect(() => {
        const loadReference = async () => {
            const [lodgeRes, eventRes] = await Promise.all([
                axios.get("/api/crud/lodges"),
                axios.get("/api/event-types/used-in-meetings"),
            ]);
            setLodges((lodgeRes.data || []).map((lodge) => ({ value: lodge.id, label: lodge.name })));
            setEventTypes((eventRes.data || []).map((event) => ({ value: event.id, label: event.description })));
        };
        loadReference().catch((err) => console.error("Failed to load meeting stat filters:", err));
    }, []);

    useEffect(() => {
        if (startDate && endDate) fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLodge, selectedEventType, excludedEventTypes, startDate, endDate]);

    const fetchStats = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const params = {};
            if (selectedLodge) params.lodge_id = selectedLodge;
            if (selectedEventType) params.eventtype_id = selectedEventType;
            if (excludedEventTypes.length) params.exclude_eventtype_ids = excludedEventTypes.join(",");
            if (startDate) params.from = startDate;
            if (endDate) params.to = endDate;
            const { data } = await axios.get("/api/stats/meetings", { params });
            setStatsData(data || {});
        } catch (err) {
            console.error("Error fetching meeting statistics:", err);
            setError("Failed to load meeting statistics.");
        } finally {
            setIsLoading(false);
        }
    };

    const visibleExcludeOptions = useMemo(
        () => eventTypes.filter((event) => event.value !== selectedEventType),
        [eventTypes, selectedEventType]
    );

    const selectedExcludedEventTypeOptions = useMemo(
        () => eventTypes.filter((event) => excludedEventTypes.includes(event.value) && event.value !== selectedEventType),
        [eventTypes, excludedEventTypes, selectedEventType]
    );

    const handleEventTypeChange = (option) => {
        const nextEventType = option?.value || "";
        setSelectedEventType(nextEventType);
        if (nextEventType) {
            setExcludedEventTypes((current) => current.filter((id) => id !== nextEventType));
        }
    };

    const monthlyRows = useMemo(() => {
        if (!startDate || !endDate) return [];
        const source = statsData.monthly_breakdown || [];
        return generateAllMonths(startDate, endDate).map((month) => {
            const rows = source.filter((row) => row.month === month);
            const meetings = rows.reduce((sum, row) => sum + Number(row.meetings || 0), 0);
            const members = rows.reduce((sum, row) => sum + Number(row.member_attendance || 0), 0);
            const visitors = rows.reduce((sum, row) => sum + Number(row.visitor_attendance || 0), 0);
            return {
                month,
                meetings,
                member_attendance: members,
                visitor_attendance: visitors,
                total_attendance: members + visitors,
                avg_attendance: meetings ? ((members + visitors) / meetings).toFixed(1) : "0.0",
            };
        });
    }, [endDate, startDate, statsData.monthly_breakdown]);

    const eventTypeMonthlyRows = useMemo(() => {
        const source = statsData.monthly_breakdown || [];
        const types = [...new Set(source.map((row) => row.eventtype_description || "Unknown"))].sort();
        const months = startDate && endDate ? generateAllMonths(startDate, endDate) : [];
        return { types, months, source };
    }, [endDate, startDate, statsData.monthly_breakdown]);

    useEffect(() => {
        if (!eventTypeChartRef.current || isLoading || error) return;
        if (eventTypeChartInstance.current) eventTypeChartInstance.current.destroy();

        const palette = [
            { members: "rgba(20, 184, 166, 0.68)", visitors: "rgba(153, 246, 228, 0.64)" },
            { members: "rgba(251, 146, 60, 0.72)", visitors: "rgba(254, 215, 170, 0.72)" },
            { members: "rgba(139, 92, 246, 0.72)", visitors: "rgba(196, 181, 253, 0.72)" },
            { members: "rgba(244, 63, 94, 0.68)", visitors: "rgba(251, 182, 206, 0.70)" },
            { members: "rgba(14, 165, 233, 0.68)", visitors: "rgba(186, 230, 253, 0.76)" },
            { members: "rgba(100, 116, 139, 0.66)", visitors: "rgba(203, 213, 225, 0.78)" },
            { members: "rgba(34, 197, 94, 0.64)", visitors: "rgba(187, 247, 208, 0.78)" },
        ];

        const activeMonths = eventTypeMonthlyRows.months.filter((month) =>
            eventTypeMonthlyRows.source.some((row) =>
                row.month === month &&
                (Number(row.member_attendance || 0) > 0 || Number(row.visitor_attendance || 0) > 0)
            )
        );

        const datasets = eventTypeMonthlyRows.types.flatMap((type, index) => {
            const colors = palette[index % palette.length];
            const rowsForMonth = (month) =>
                eventTypeMonthlyRows.source.filter((row) =>
                    row.month === month && (row.eventtype_description || "Unknown") === type
                );
            return [
                {
                    label: `Members - ${type}`,
                    data: activeMonths.map((month) =>
                        rowsForMonth(month).reduce((sum, row) => sum + Number(row.member_attendance || 0), 0)
                    ),
                    backgroundColor: colors.members,
                    borderColor: colors.members.replace(/0\.\d+\)/, "0.95)"),
                    borderWidth: 1,
                    stack: "monthly-attendance",
                },
                {
                    label: `Visitors - ${type}`,
                    data: activeMonths.map((month) =>
                        rowsForMonth(month).reduce((sum, row) => sum + Number(row.visitor_attendance || 0), 0)
                    ),
                    backgroundColor: colors.visitors,
                    borderColor: colors.visitors.replace(/0\.\d+\)/, "0.95)"),
                    borderWidth: 1,
                    stack: "monthly-attendance",
                },
            ];
        });

        eventTypeChartInstance.current = new Chart(eventTypeChartRef.current.getContext("2d"), {
            type: "bar",
            data: {
                labels: activeMonths,
                datasets,
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                scales: {
                    x: {
                        stacked: true,
                        ticks: { maxRotation: 55, minRotation: 35 },
                    },
                    y: { stacked: true, beginAtZero: true, title: { display: true, text: "Attendance Count" } },
                },
                plugins: {
                    legend: {
                        position: "top",
                        labels: { boxWidth: 32, usePointStyle: false },
                    },
                    title: { display: true, text: "Monthly Attendance by Member/Visitor and Meeting Type" },
                },
            },
        });

        return () => {
            if (eventTypeChartInstance.current) eventTypeChartInstance.current.destroy();
        };
    }, [error, eventTypeMonthlyRows, isLoading]);

    const kpis = [
        ["Meetings", statsData.total_meetings || 0],
        ["Total Attendance", statsData.total_attendance || 0],
        ["Members", statsData.member_attendance || 0],
        ["Visitors", statsData.visitor_attendance || 0],
        ["Avg / Meeting", statsData.average_attendance || 0],
    ];

    const prepareSankeyModal = () => {
        const nodeSet = new Set();
        const links = [];
        (statsData.visitor_flows || []).forEach((flow) => {
            const source = flow.visitor_lodge;
            const target = flow.meeting_lodge;
            const value = Number(flow.visitor_count) || 0;
            if (!source || !target || value <= 0) return;
            nodeSet.add(source);
            nodeSet.add(target);
            links.push({ source, target, value });
        });
        setSankeyData({ nodes: [...nodeSet].map((name) => ({ name })), links });
        setSankeyModal(true);
    };

    const exportVisitorDates = () => {
        exportCsv(
            "meeting-visitors.csv",
            ["Date", "Visitor", "Visitor Lodge", "Meeting Lodge", "Event Type"],
            (statsData.visitor_dates || []).map((row) => [
                row.meeting_date,
                row.visitor_name || "Unknown",
                row.visitor_lodge || "",
                row.meeting_lodge || "",
                row.event_type || "",
            ])
        );
    };

    const exportMonthly = () => {
        exportCsv(
            "meeting-monthly-stats.csv",
            ["Month", "Meetings", "Member Attendance", "Visitor Attendance", "Total Attendance", "Average Attendance"],
            monthlyRows.map((row) => [row.month, row.meetings, row.member_attendance, row.visitor_attendance, row.total_attendance, row.avg_attendance])
        );
    };

    const exportEventTypeMonthly = () => {
        exportCsv(
            "meeting-types-by-month.csv",
            ["Month", "Event Type", "Meetings", "Member Attendance", "Visitor Attendance", "Average Attendance"],
            (statsData.monthly_breakdown || []).map((row) => [
                row.month,
                row.eventtype_description || "Unknown",
                row.meetings,
                row.member_attendance,
                row.visitor_attendance,
                row.avg_attendance,
            ])
        );
    };

    const exportVisitorLodges = () => {
        exportCsv(
            "meeting-top-visitor-lodges.csv",
            ["Rank", "Visitor Lodge", "Visitors"],
            (statsData.top_visitor_lodges || []).map((row, index) => [index + 1, row.name, row.visitor_count])
        );
    };

    const exportTopVisitors = () => {
        exportCsv(
            "meeting-top-visitors.csv",
            ["Rank", "Name", "Visitor Lodge", "Visits", "First Visit", "Last Visit"],
            (statsData.top_visitors || []).map((row, index) => [
                index + 1,
                row.member_name || "Unknown",
                row.visitor_lodge || "",
                row.visit_count,
                formatDate(row.first_visit),
                formatDate(row.last_visit),
            ])
        );
    };

    const exportRankedMeetings = () => {
        exportCsv(
            "meeting-ranked-meetings.csv",
            ["Rank", "Date", "Lodge", "Event", "Description", "Total Attendance", "Members", "Visitors"],
            (statsData.ranked_meetings || []).map((row, index) => [
                index + 1,
                row.meeting_date,
                row.lodge_name,
                row.event_type,
                row.description || "",
                row.total_attendance,
                row.member_attendance,
                row.visitor_attendance,
            ])
        );
    };

    const exportLodgeRankings = () => {
        exportCsv(
            "meeting-lodge-rankings.csv",
            ["Rank", "Lodge", "Meetings", "Average Attendance"],
            (statsData.lodge_breakdown || []).map((row, index) => [index + 1, row.name, row.meetings, row.avg_attendance])
        );
    };

    const exportEventTypeRankings = () => {
        exportCsv(
            "meeting-event-type-rankings.csv",
            ["Rank", "Event Type", "Meetings", "Average Attendance"],
            (statsData.eventtype_breakdown || []).map((row, index) => [index + 1, row.description, row.meetings, row.avg_attendance])
        );
    };

    const exportAllMeetingStats = () => {
        exportCsv(
            "meeting-statistics-complete.csv",
            ["Section", "Field 1", "Field 2", "Field 3", "Field 4", "Field 5", "Field 6", "Field 7"],
            [
                ["Summary", "Meetings", statsData.total_meetings || 0, "Total Attendance", statsData.total_attendance || 0, "Visitors", statsData.visitor_attendance || 0],
                ...monthlyRows.map((row) => ["Monthly", row.month, row.meetings, row.member_attendance, row.visitor_attendance, row.total_attendance, row.avg_attendance, ""]),
                ...(statsData.monthly_breakdown || []).map((row) => ["Monthly Event Type", row.month, row.eventtype_description || "Unknown", row.meetings, row.member_attendance, row.visitor_attendance, row.avg_attendance, ""]),
                ...(statsData.top_visitor_lodges || []).map((row, index) => ["Top Visitor Lodge", index + 1, row.name, row.visitor_count, "", "", "", ""]),
                ...(statsData.top_visitors || []).map((row, index) => ["Top Visitor", index + 1, row.member_name || "Unknown", row.visitor_lodge || "", row.visit_count, formatDate(row.first_visit), formatDate(row.last_visit), ""]),
                ...(statsData.visitor_dates || []).map((row) => ["Visitor Date", row.meeting_date, row.visitor_name || "Unknown", row.visitor_lodge || "", row.meeting_lodge || "", row.event_type || "", "", ""]),
                ...(statsData.ranked_meetings || []).map((row, index) => ["Ranked Meeting", index + 1, row.meeting_date, row.lodge_name, row.event_type, row.total_attendance, row.member_attendance, row.visitor_attendance]),
                ...(statsData.lodge_breakdown || []).map((row, index) => ["Lodge Ranking", index + 1, row.name, row.meetings, row.avg_attendance, "", "", ""]),
                ...(statsData.eventtype_breakdown || []).map((row, index) => ["Event Type Ranking", index + 1, row.description, row.meetings, row.avg_attendance, "", "", ""]),
            ]
        );
    };

    return (
        <div className="st-modal-overlay">
            <div className="st-modal-content">
                <header className="st-modal-header">
                    <div>
                        <p className="st-eyebrow">Archive Analytics</p>
                        <h2>Meeting Statistics</h2>
                    </div>
                    <button className="st-modal-close-btn" onClick={onClose} aria-label="Close Modal">&times;</button>
                </header>

                <div className="st-modal-body st-dashboard">
                    <aside className="st-filter-panel">
                        <h3>Filters</h3>
                        <label>Start Date</label>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        <label>End Date</label>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        <label>Lodge</label>
                        <Select
                            options={[{ value: "", label: "All Lodges" }, ...lodges]}
                            value={lodges.find((lodge) => lodge.value === selectedLodge) || { value: "", label: "All Lodges" }}
                            onChange={(option) => setSelectedLodge(option?.value || "")}
                            isClearable
                            isSearchable
                        />
                        <label>Event Type</label>
                        <Select
                            options={[{ value: "", label: "All Types" }, ...eventTypes]}
                            value={eventTypes.find((event) => event.value === selectedEventType) || { value: "", label: "All Types" }}
                            onChange={handleEventTypeChange}
                            isClearable
                            isSearchable
                        />
                        <label>Hide Event Types</label>
                        <Select
                            options={visibleExcludeOptions}
                            value={selectedExcludedEventTypeOptions}
                            onChange={(options) => setExcludedEventTypes((options || []).map((option) => option.value))}
                            placeholder="Remove classes..."
                            isMulti
                            isClearable
                            isSearchable
                            closeMenuOnSelect={false}
                        />
                        <button className="st-primary-btn" onClick={fetchStats}>Refresh</button>
                        <button className="st-secondary-btn" onClick={prepareSankeyModal}>Visitor Flow Sankey</button>
                        <button className="st-secondary-btn" onClick={exportAllMeetingStats}>Export Everything CSV</button>
                        <button className="st-secondary-btn" onClick={exportMonthly}>Export Monthly CSV</button>
                        <button className="st-secondary-btn" onClick={exportEventTypeMonthly}>Export Type/Month CSV</button>
                        <button className="st-secondary-btn" onClick={exportVisitorDates} disabled={!(statsData.visitor_dates || []).length}>Export Visitors CSV</button>
                    </aside>

                    <main className="st-main-panel">
                        {isLoading && <div className="loading-spinner">Loading...</div>}
                        {error && <div className="error-message">{error}</div>}
                        {!isLoading && !error && (
                            <>
                                <section className="st-kpi-grid">
                                    {kpis.map(([label, value]) => (
                                        <div key={label} className="st-kpi">
                                            <strong>{value}</strong>
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                </section>

                                <section className="st-panel">
                                    <div className="st-panel-heading">
                                        <h3>Monthly Attendance Mix</h3>
                                        <div className="st-panel-actions">
                                            <span>{monthlyRows.filter((row) => row.meetings).length} active months · {eventTypeMonthlyRows.types.length} types</span>
                                            <button className="st-mini-btn" onClick={exportEventTypeMonthly}>Export</button>
                                        </div>
                                    </div>
                                    <div className="st-chart-container st-chart-container-large">
                                        <canvas ref={eventTypeChartRef}></canvas>
                                    </div>
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Top Visitor Lodges</h3>
                                            <div className="st-panel-actions">
                                                <span>Ranked by visits</span>
                                                <button className="st-mini-btn" onClick={exportVisitorLodges}>Export</button>
                                            </div>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Lodge", "Visitors"]}
                                            rows={(statsData.top_visitor_lodges || []).map((row, index) => [index + 1, row.name, row.visitor_count])}
                                            empty="No visitor lodge data."
                                        />
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Top Visitors</h3>
                                            <div className="st-panel-actions">
                                                <span>Names and dates</span>
                                                <button className="st-mini-btn" onClick={exportTopVisitors}>Export</button>
                                            </div>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Name", "Lodge", "Visits", "Last Visit"]}
                                            rows={(statsData.top_visitors || []).map((row, index) => [
                                                index + 1,
                                                row.member_name || "Unknown",
                                                row.visitor_lodge || "",
                                                row.visit_count,
                                                formatDate(row.last_visit),
                                            ])}
                                            empty="No named visitors found."
                                        />
                                    </div>
                                </section>

                                <section className="st-panel">
                                    <div className="st-panel-heading">
                                        <h3>Visitor Dates</h3>
                                        <div className="st-panel-actions">
                                            <span>{(statsData.visitor_dates || []).length} recent visitor records</span>
                                            <button className="st-mini-btn" onClick={exportVisitorDates}>Export</button>
                                        </div>
                                    </div>
                                    <StatsTable
                                        headers={["Date", "Visitor", "Visitor Lodge", "Meeting Lodge", "Event"]}
                                        rows={(statsData.visitor_dates || []).map((row) => [
                                            row.meeting_date,
                                            row.visitor_name || "Unknown",
                                            row.visitor_lodge || "",
                                            row.meeting_lodge || "",
                                            row.event_type || "",
                                        ])}
                                        empty="No visitor dates in this range."
                                    />
                                </section>

                                <section className="st-panel">
                                    <div className="st-panel-heading">
                                        <h3>Ranked Meetings</h3>
                                        <div className="st-panel-actions">
                                            <span>Highest attendance</span>
                                            <button className="st-mini-btn" onClick={exportRankedMeetings}>Export</button>
                                        </div>
                                    </div>
                                    <StatsTable
                                        headers={["#", "Date", "Lodge", "Event", "Total", "Members", "Visitors"]}
                                        rows={(statsData.ranked_meetings || []).map((row, index) => [
                                            index + 1,
                                            row.meeting_date,
                                            row.lodge_name,
                                            row.event_type,
                                            row.total_attendance,
                                            row.member_attendance,
                                            row.visitor_attendance,
                                        ])}
                                        empty="No ranked meetings available."
                                    />
                                </section>

                                <section className="st-grid-two">
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Lodge Rankings</h3>
                                            <div className="st-panel-actions">
                                                <span>Meetings and average attendance</span>
                                                <button className="st-mini-btn" onClick={exportLodgeRankings}>Export</button>
                                            </div>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Lodge", "Meetings", "Avg Attendance"]}
                                            rows={(statsData.lodge_breakdown || []).map((row, index) => [index + 1, row.name, row.meetings, row.avg_attendance])}
                                            empty="No lodge breakdown."
                                        />
                                    </div>
                                    <div className="st-panel">
                                        <div className="st-panel-heading">
                                            <h3>Event Type Rankings</h3>
                                            <div className="st-panel-actions">
                                                <span>Meeting mix</span>
                                                <button className="st-mini-btn" onClick={exportEventTypeRankings}>Export</button>
                                            </div>
                                        </div>
                                        <StatsTable
                                            headers={["#", "Event Type", "Meetings", "Avg Attendance"]}
                                            rows={(statsData.eventtype_breakdown || []).map((row, index) => [index + 1, row.description, row.meetings, row.avg_attendance])}
                                            empty="No event type breakdown."
                                        />
                                    </div>
                                </section>
                            </>
                        )}
                    </main>
                </div>

                {sankeyModal && (
                    <div className="sankey-modal-overlay">
                        <div className="sankey-modal-content">
                            <div className="sankey-modal-header">
                                <h2>Visitor Lodge Flow</h2>
                                <button onClick={() => setSankeyModal(false)} aria-label="Close Sankey Modal">&times;</button>
                                <button
                                    className="sankey-save-button"
                                    onClick={() => {
                                        const chart = echartRef.current.getEchartsInstance();
                                        const imgBase64 = chart.getDataURL({ type: "png", backgroundColor: "#ffffff" });
                                        const link = document.createElement("a");
                                        link.href = imgBase64;
                                        link.download = "visitor-flow-sankey.png";
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                    }}
                                >
                                    Save Chart
                                </button>
                            </div>
                            <div className="sankey-modal-body">
                                <div className="sankey-controls">
                                    <button onClick={() => setChartSize((prev) => ({ width: Math.max(500, prev.width - 160), height: Math.max(360, prev.height - 100) }))}>Reduce Size</button>
                                    <button onClick={() => setChartSize((prev) => ({ width: prev.width + 160, height: prev.height + 100 }))}>Increase Size</button>
                                </div>
                                <ReactECharts
                                    ref={echartRef}
                                    option={{
                                        tooltip: { trigger: "item" },
                                        series: [{
                                            type: "sankey",
                                            data: sankeyData.nodes,
                                            links: sankeyData.links,
                                            emphasis: { focus: "adjacency" },
                                            lineStyle: { color: "gradient", curveness: 0.5 },
                                        }],
                                    }}
                                    style={{ width: `${chartSize.width}px`, height: `${chartSize.height}px` }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatsTable({ headers, rows, empty }) {
    return (
        <div className="st-table-wrap">
            <table className="st-stats-table">
                <thead>
                <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
                </thead>
                <tbody>
                {rows.length ? rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
                    </tr>
                )) : (
                    <tr><td colSpan={headers.length}>{empty}</td></tr>
                )}
                </tbody>
            </table>
        </div>
    );
}

export default StatsMeetings;
